import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api.js';

/**
 * Minimal async-data hook.
 *
 * Deliberately not a data-fetching library: this app has a handful of screens
 * and no cache-invalidation problem worth 40 kB of dependency. `reload` is what
 * a mutation calls when it needs the screen to catch up.
 *
 * Results are stored against the request key they belong to, and `loading` is
 * derived from whether the stored key matches the current one. That means no
 * state is set synchronously inside the effect — which avoids a cascading
 * render — and a slow response for stale inputs can never overwrite a newer
 * one.
 */
export interface AsyncState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

interface Settled<T> {
  key: string;
  data: T | null;
  error: ApiError | null;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', 'Something went wrong.', null);
}

export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[]): AsyncState<T> {
  const [nonce, setNonce] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);

  const key = `${JSON.stringify(deps)}:${String(nonce)}`;

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    loader()
      .then((data) => {
        if (!cancelled) setSettled({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setSettled({ key, data: null, error: toApiError(err) });
      });

    return () => {
      cancelled = true;
    };
    // `loader` is intentionally excluded: callers pass an inline closure that is
    // a new function every render, and `deps` already describes what it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const current = settled?.key === key ? settled : null;

  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    loading: current === null,
    reload,
  };
}

/** Tracks an in-flight mutation and surfaces its failure to the UI. */
export function useAction(): {
  run: (fn: () => Promise<void>) => Promise<void>;
  pending: boolean;
  error: string | null;
  clearError: () => void;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (fn: () => Promise<void>) => {
    setPending(true);
    setError(null);
    try {
      await fn();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setPending(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { run, pending, error, clearError };
}
